import { useToast } from 'context/tostifyContext';
import React, { useEffect, useState } from 'react';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../constants/ActionTypes';

import { useDispatch } from 'react-redux';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';

const EARTH_RADIUS = 6378137;

const useQuickContract = () => {
  const [formData, setFormData] = useState({
    contractType: 'Contract Haulage',
    description: '',
    dropPinLocation: '',
    product: '',
    entrance: '',
    destination: '',
    leeway: 500,
    startDate: '',
    finishDate: '',
    customerDetails: '',
    customerName: '',
    address: '',
    estimatedVolume: '',
    volume: '',
    measurementType: '',
    loads: '',
    haulage: '',
  });

  const [errors, setErrors] = useState({});

  const [products, setProducts] = useState([]);
  const [destinationName, setDestinationName] = useState([]);
  const [customerName, setCustomerName] = useState([]);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { showToast } = useToast();
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchDropdown = async () => {
      try {
        const headers = { headers: authHeader() };

        const [productRes, destinationRes] = await Promise.all([
          customAxios.get('/Ops/GetProducts', headers),
          customAxios.get('/CRM/GetAllCustomerLocations', headers),
        ]);

        setProducts(productRes.data.map(item => item.productName));

        const destData = destinationRes.data;
        setDestinationName(destData.map(item => item.destinationName));
        setCustomerName(destData.map(item => item.customerName));
      } catch (err) {
        console.log(err);
      }
    };

    fetchDropdown();
  }, []);

  function extractLatLng(url) {
    try {
      // Support ?q=lat,lng
      const parsedUrl = new URL(url);
      const q = parsedUrl.searchParams.get('q');

      if (q) {
        const [lat, lng] = q.split(',').map(Number);

        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    } catch (_) {
      // Ignore invalid URL
    }

    // Support @lat,lng
    let match = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) {
      return {
        lat: Number(match[1]),
        lng: Number(match[2]),
      };
    }

    // Support !3dlat!4dlng
    match = url.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
    if (match) {
      return {
        lat: Number(match[1]),
        lng: Number(match[2]),
      };
    }

    // Support bare "lat,lng"
    match = url.trim().match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);
    if (match) {
      return {
        lat: Number(match[1]),
        lng: Number(match[2]),
      };
    }

    return null;
  }

  function offsetPoint(lat, lng, dx, dy) {
    const newLat = lat + (dy / EARTH_RADIUS) * (180 / Math.PI);
    const newLng = lng + (dx / (EARTH_RADIUS * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);

    return { lat: newLat, lng: newLng };
  }

  /**
   * Generates 4 corner points (N, E, S, W) for a given radius
   */
  function generateFourPoints(lat, lng, radius) {
    return [
      offsetPoint(lat, lng, 0, radius),
      offsetPoint(lat, lng, radius, 0),
      offsetPoint(lat, lng, 0, -radius),
      offsetPoint(lat, lng, -radius, 0),
    ];
  }

  /**
   * Creates API-ready polygon payloads for 300m, 200m, 100m rings
   */
  function generateSitePolygons(lat, lng, siteAssessmentID) {
    const boundary300 = generateFourPoints(lat, lng, 300);
    const subCompartment200 = generateFourPoints(lat, lng, 200);
    const stacking100 = generateFourPoints(lat, lng, 100);

    const makeItem = (point, sortOrder, label, color) => ({
      siteAssessmentID,
      mapLabel: label,
      latitude: point.lat,
      longitude: point.lng,
      hexColor: color,
      mapType: 'polygon',
      sortOrder,
      displayName: label,
      relatedTo: null,
      area: null,
    });

    const payload = [];

    // 300m Boundary
    boundary300.forEach((p, i) => payload.push(makeItem(p, i, 'Boundary 1', 'white')));

    // 200m Sub-Compartment
    subCompartment200.forEach((p, i) => payload.push(makeItem(p, i, 'Sub Compartments 1', 'purple')));

    // 100m Stacking Area
    stacking100.forEach((p, i) => payload.push(makeItem(p, i, 'Stacking Area 1', 'orange')));

    return payload;
  }

  const handleQuickContract = async () => {
    //  Handling fields validation
    if (!formData.dropPinLocation) {
      showToast('Please enter the dropPin location', 'error');
      return;
    } else if (!formData.description) {
      showToast('Please enter the Site Ref', 'error');
      return;
    } else if (!formData.destination) {
      showToast('Please enter the destination', 'error');
      return;
    } else if (!formData.leeway) {
      showToast('Please enter the Leeway detail');
      return;
    }

    try {
      dispatch({ type: FETCH_START });

      const coordinates = extractLatLng(formData.dropPinLocation);
      if (!coordinates) {
        showToast('Invalid dropPin link. Unable to get Lat and Lng. Please check the link', 'error');
        return;
      }

      const headers = {
        ...authHeader(),
        'Content-Type': 'multipart/form-data',
      };

      const fd = new FormData();
      fd.append('ref', formData.description);
      fd.append('siteAssessmentType', 'Contract Work');
      fd.append('siteAssessmentCategory', 'Haulage');
      fd.append('totalVolume', formData.volume);

      const res = await customAxios.post('/CRM/AddCRMSiteDetails', fd, { headers });
      const newSiteId = res?.data?.newSiteAssessmentID;

      if (!newSiteId) {
        showToast('Failed to create site: no site ID returned', 'error');
        return;
      }

      const polygons = generateSitePolygons(coordinates.lat, coordinates.lng, newSiteId);

      const addMapDetailsPayload = [
        {
          siteAssessmentID: newSiteId,
          mapLabel: 'Entrance 1',
          latitude: coordinates.lat,
          longitude: coordinates.lng,
          hexColor: 'blue',
          mapType: 'pin',
          sortOrder: 0,
          displayName: 'Entrance 1',
          relatedTo: null,
          area: 0,
        },
        ...polygons,
      ];

      const addProductPayload = formData.product.map(product => ({
        siteAssessmentID: newSiteId,
        products: product,
        productsLen: '',
        productsMinDia: '',
        productsPercent: 50,
        productsDestination: formData.destination,
        productsPricePerTonne: 0,
        price: 0,
        subCompartmentName: 'subcompartment 1',
      }));

      // Group 1 — independent, parallelize
      await Promise.all([
        customAxios.post('/SiteDetails/AddSiteDetailsMapCoordinates', addMapDetailsPayload, { headers: authHeader() }),
        customAxios.post('/SiteDetails/AddSiteDetailsProducts', addProductPayload, { headers: authHeader() }),
      ]);

      await customAxios.post('/Ops/AddDefaultTimberPricing', { siteAssessmentID: newSiteId }, { headers: authHeader() });

      const payload = {
        fellingLicenseNo: null,
        siteAssessmentID: newSiteId,
        pricingVersion: `${newSiteId}-0`,
      };

      const finalResponse = await customAxios.post('/Ops/ConvertTimberPricingToContract', payload, {
        headers: authHeader(),
      });
      const newContractUniqueID = finalResponse?.data?.newContractUniqueID;

      const geoFencePayload = {
        siteAssessmentID: newSiteId,
        contractUniqueID: newContractUniqueID,
        locationName: formData.description,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        radius: 1000,
        mapType: 'pin',
        geofenceType: 'site',
      };

      // Group 2 — the edit-contract chain and geofencing don't depend on each other
      await Promise.all([
        (async () => {
          const createdContract = await customAxios.get(
            `/Ops/GetContracts?flagActive=1&contractUniqueID=${newContractUniqueID}`,
            { headers: authHeader() },
          );
          const contractData = createdContract.data?.[0];
          if (!contractData) {
            throw new Error('Failed to fetch created contract details');
          }
          return customAxios.post(
            '/Ops/EditContract',
            {
              ...contractData,
              contractUniqueID: newContractUniqueID,
              contractType: 'Contract Work',
              purchaseType: 'Haulage',
              wH_NumberName: formData.description,
            },
            { headers: authHeader() },
          );
        })(),
        customAxios.post('/Ops/AddGeofencingData', geoFencePayload, { headers: authHeader() }),
      ]);

      await updateRecordModifiedDate(newSiteId);

      dispatch({ type: FETCH_SUCCESS });
      setSuccessMessage({ id: newSiteId, ref: formData.description, contract: newContractUniqueID });
      setOpenSuccessDialog(true);

      // showToast('Quick Contract created successfully');
    } catch (err) {
      dispatch({ type: FETCH_ERROR, payload: 'Failed to create Quick contract' });
      const message =
        err?.response?.data || err?.message || 'An unexpected error occurred while creating the quick contract';
      showToast(message, 'error');
      throw err;
    }
  };

  const handleChange = field => event => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }

    // Auto-fill entrance with drop pin location
    if (field === 'dropPinLocation') {
      setFormData(prev => ({
        ...prev,
        entrance: value,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.contractType) newErrors.contractType = 'Contract type is required';
    if (!formData.haulage) newErrors.haulage = 'Haulage is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (validateForm()) {
      console.log('Form submitted:', formData);
      // Handle form submission here
      alert('Form submitted successfully! Check console for data.');
    }
  };

  const resetForm = () => {
    setFormData({
      contractType: 'Contract Haulage',
      description: '',
      dropPinLocation: '',
      product: '',
      entrance: '',
      destination: '',
      leeway: 500,
      startDate: '',
      finishDate: '',
      customerDetails: '',
      customerName: '',
      address: '',
      estimatedVolume: '',
      volume: '',
      measurementType: '',
      loads: '',
      haulage: '',
    });
    setErrors({});
  };

  return {
    formData,
    errors,
    handleChange,
    handleSubmit,
    resetForm,
    products,
    destinationName,
    customerName,
    handleQuickContract,
    successMessage,
    openSuccessDialog,
    setOpenSuccessDialog,
  };
};

export default useQuickContract;
